import { ElementType } from 'react';
import {
  Assignment,
  CompareArrows,
  Description,
  EmojiPeople,
  EventAvailable,
  Explore,
  LibraryBooks,
} from '@material-ui/icons';

export interface EntityCount {
  entityType: string;
  count: number;
}

interface EntityLabel {
  singular: string;
  plural: string;
}

/**
 * The entity types worth showing an admin, in display order. The copy engine
 * maps far more than this (items, fields, options), which is detail nobody
 * managing sub-accounts needs to see.
 */
const HEADLINE_ENTITY_LABELS: Array<[string, EntityLabel]> = [
  ['program', { singular: 'program', plural: 'programs' }],
  ['task', { singular: 'task', plural: 'tasks' }],
  ['activity', { singular: 'activity', plural: 'activities' }],
  ['form', { singular: 'form', plural: 'forms' }],
  ['support_resource', { singular: 'resource', plural: 'resources' }],
  ['matching_rule', { singular: 'matching rule', plural: 'matching rules' }],
];

export const headlineEntityCounts = (counts: EntityCount[]): Array<{ label: string; count: number }> => {
  const byType = new Map(counts.map((entityCount) => [entityCount.entityType, entityCount.count]));

  return HEADLINE_ENTITY_LABELS.map(([entityType, label]) => {
    const count = byType.get(entityType) ?? 0;

    return { label: count === 1 ? label.singular : label.plural, count };
  }).filter((entry) => entry.count > 0);
};

export const formatHeadlineEntityCounts = (counts: EntityCount[]): string => {
  const headline = headlineEntityCounts(counts);

  if (headline.length === 0) {
    return 'No entities copied yet';
  }

  return headline.map((entry) => `${entry.count} ${entry.label}`).join(' · ');
};

const TYPE_HEADINGS = new Map<string, string>([
  ['program', 'Programs'],
  ['activity_role', 'Roles'],
  ['task', 'Tasks'],
  ['form', 'Forms'],
  ['support_resource', 'Resources'],
  ['activity', 'Activities'],
  ['matching_rule', 'Matching Rules'],
]);

export const entityTypeHeading = (entityType: string): string =>
  TYPE_HEADINGS.get(entityType) ?? entityType.replace(/_/g, ' ');

const TYPE_ICONS = new Map<string, ElementType>([
  ['program', Explore],
  ['activity_role', EmojiPeople],
  ['task', Assignment],
  ['form', Description],
  ['support_resource', LibraryBooks],
  ['activity', EventAvailable],
  ['matching_rule', CompareArrows],
]);

export const entityTypeIcon = (entityType: string): ElementType => TYPE_ICONS.get(entityType) ?? Assignment;
